import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
    Megaphone, CheckCircle, DollarSign, Target,
    Heart, MessageCircle, Repeat2, Users, Image, Film
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { apiUrl } from '../../config/Config';
import PerformanceTrendsChart from '../../components/Charts/PerformanceTrendsChart';
import BudgetAllocationChart  from '../../components/Charts/BudgetAllocationChart';
// --- Sub-components ---
const commonMetricsData = {
    totalSpend: { value: '$15,840', change: 5.2, isPositive: true },
    totalImpressions: { value: '88,920', change: 5.7, isPositive: true },
    totalClicks: { value: '88,920', change: 7.4, isPositive: true },
    averageCTR: { value: '7.41%', change: 10, isPositive: true },
};

const PerformanceMetric = ({ title, value, change, isPositive, icon: Icon }) => (
    <div className="marketing-dashboard-ui-metric-card">
        <div className="marketing-dashboard-ui-metric-header">
            {Icon && <Icon size={24} className="marketing-dashboard-ui-metric-icon" />}
            <div className="marketing-dashboard-ui-metric-title">{title}</div>
        </div>
        <div className="marketing-dashboard-ui-metric-value">{value}</div>
        {change !== undefined && (
            <div className={`marketing-dashboard-ui-percentage ${isPositive ? 'positive' : 'negative'}`}>
                {isPositive ? '+' : ''}{change}% {isPositive ? '↑' : '↓'}
            </div>
        )}
    </div>
);

// --- Post Dashboard Data ---
const postPerformanceData = [
    { name: 'Post 1', Likes: 1200, Comments: 250, Shares: 80, Reach: 15000 },
    { name: 'Post 2', Likes: 850, Comments: 180, Shares: 50, Reach: 10000 },
    { name: 'Post 3', Likes: 1500, Comments: 300, Shares: 100, Reach: 20000 },
    { name: 'Post 4', Likes: 900, Comments: 100, Shares: 40, Reach: 12000 },
    { name: 'Post 5', Likes: 1100, Comments: 220, Shares: 70, Reach: 16000 },
];

const topPostsData = [
    { id: 1, title: 'Summer Sale Announcement!', type: 'Image', reach: '25K', engagement: '1.2K', date: '2023-07-15' },
    { id: 2, title: 'New Product Launch Video', type: 'Video', reach: '30K', engagement: '2.5K', date: '2023-07-10' },
    { id: 3, title: 'Behind the Scenes: Our Team', type: 'Image', reach: '18K', engagement: '900', date: '2023-07-20' },
];

// --- Post Dashboard View ---
const PostDashboardView = () => (
    <div className="marketing-dashboard-ui-sub-dashboard-content">
        <div className="marketing-dashboard-ui-metrics-row" style={{ marginTop: '30px' }}>
            <PerformanceMetric
                title="Total Likes"
                value={commonMetricsData.totalSpend.value}
                change={commonMetricsData.totalSpend.change}
                isPositive={commonMetricsData.totalSpend.isPositive}
                icon={Heart}
            />
            <PerformanceMetric
                title="Total Comments"
                value={commonMetricsData.totalImpressions.value}
                change={commonMetricsData.totalImpressions.change}
                isPositive={commonMetricsData.totalImpressions.isPositive}
                icon={MessageCircle}
            />
            <PerformanceMetric
                title="Total Shares"
                value={commonMetricsData.totalClicks.value}
                change={commonMetricsData.totalClicks.change}
                isPositive={commonMetricsData.totalClicks.isPositive}
                icon={Repeat2}
            />
            <PerformanceMetric
                title="Avg. Reach"
                value={commonMetricsData.averageCTR.value}
                change={commonMetricsData.averageCTR.change}
                isPositive={commonMetricsData.averageCTR.isPositive}
                icon={Users}
            />
        </div>

        <div className="marketing-dashboard-ui-charts-row" style={{ marginTop: '20px' }}>
            <div className="marketing-dashboard-ui-chart-container" style={{ flex: 2 }}>
                <div className="marketing-dashboard-ui-chart-header">
                    <p className="marketing-dashboard-ui-chart-title">Post Engagement Trends</p>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={postPerformanceData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="left" stroke="#ef4444" label={{ value: 'Likes/Comments', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" label={{ value: 'Reach', angle: 90, position: 'insideRight' }} />
                        <Tooltip />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="Likes" stroke="#ef4444" strokeWidth={2} />
                        <Line yAxisId="left" type="monotone" dataKey="Comments" stroke="#f59e0b" strokeWidth={2} />
                        <Line yAxisId="right" type="monotone" dataKey="Reach" stroke="#3b82f6" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="marketing-dashboard-ui-budget-card" style={{ flex: 1 }}>
                <p className="marketing-dashboard-ui-budget-title">Top Performing Posts</p>
                <table className="marketing-dashboard-ui-table">
                    <thead>
                        <tr>
                            <th>Post</th>
                            <th>Type</th>
                            <th>Engagement</th>
                        </tr>
                    </thead>
                    <tbody>
                        {topPostsData.map(post => (
                            <tr key={post.id}>
                                <td>{post.title}</td>
                                <td>{post.type === 'Image' ? <Image size={16} /> : <Film size={16} />}</td>
                                <td>{post.engagement}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);

// --- Campaign Dashboard View ---
const CampaignDashboardView = ({ dashboardCounts, recentCampaigns, weeklyData, selectedAccount }) => {
    const [budgetAllocationData, setBudgetAllocationData] = useState([]);
    const [datewiseInsights, setDatewiseInsights] = useState([]);
    const userdata = useSelector(store => store.userReducer.userdata);
const [budgetDropdownOpen, setBudgetDropdownOpen] = useState(false);
const [selectedBudgetRange, setSelectedBudgetRange] = useState("last_30_days");
    const fetchLastSectionData = async (adAccountId) => {
        try {
            const budgetRes = await axios.get(apiUrl + 'facebook_budget_allocation', {
                params: { ad_account_id: adAccountId, range_type: selectedBudgetRange },
                headers: { Authorization: 'Bearer ' + userdata?.token }
            });
            setBudgetAllocationData(budgetRes.data?.data?.data?.packs || []);

            const datewiseRes = await axios.get(apiUrl + 'facebook_datewise_combined_insights', {
                params: { ad_account_id: adAccountId, range_type: 'last_7_days' },
                headers: { Authorization: 'Bearer ' + userdata?.token }
            });
            setDatewiseInsights(datewiseRes.data?.data?.data?.data || []);
        } catch (err) {
            console.error('❌ Fetch Last Section Data Error', err);
        }
    };

    useEffect(() => {
        if (selectedAccount) {
            fetchLastSectionData(selectedAccount);
        }
    }, [selectedAccount]);

    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

    return (
        <div className="marketing-dashboard-ui-sub-dashboard-content">
            {/* Campaign Summary Cards */}
            <div className="marketing-dashboard-ui-campaign-summary-grid">
                <div className="marketing-dashboard-ui-campaign-summary-card">
                    <Megaphone size={24} />
                    <h4>Active Campaigns</h4>
                    <p>{dashboardCounts.active_campaigns}</p>
                </div>
                <div className="marketing-dashboard-ui-campaign-summary-card">
                    <CheckCircle size={24} />
                    <h4>Completed Campaigns</h4>
                    <p>{dashboardCounts.completed_campaigns}</p>
                </div>
                <div className="marketing-dashboard-ui-campaign-summary-card">
                    <DollarSign size={24} />
                    <h4>Total Budget Used</h4>
                    <p>${dashboardCounts.total_budget_used}</p>
                </div>
                <div className="marketing-dashboard-ui-campaign-summary-card">
                    <Target size={24} />
                    <h4>Avg. Conversion Rate</h4>
                    <p>{dashboardCounts.avg_conversion_rate?.toFixed(2)}%</p>
                </div>
            </div>

            {/* Weekly Campaign Trends & Recent Campaigns */}
            <div className="marketing-dashboard-ui-charts-row" style={{ marginTop: '20px' }}>
                <div className="marketing-dashboard-ui-chart-container" >
                    <div className="marketing-dashboard-ui-chart-header">
                        <p className="marketing-dashboard-ui-chart-title">Weekly Campaign Trends</p>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={weeklyData.weeks?.map((week, i) => ({
                            name: week,
                            Clicks: weeklyData.clicks[i] || 0,
                            Conversions: weeklyData.conversions[i] || 0
                        })) || []} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis dataKey="name" />
                            <YAxis yAxisId="left" stroke="#3b82f6" label={{ value: 'Clicks', angle: -90, position: 'insideLeft' }} />
                            <YAxis yAxisId="right" orientation="right" stroke="#10b981" label={{ value: 'Conversions', angle: 90, position: 'insideRight' }} />
                            <Tooltip />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="Clicks" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                            <Line yAxisId="right" type="monotone" dataKey="Conversions" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Recent Campaigns Table */}
                <div className="marketing-dashboard-ui-budget-card" style={{ flex: 1 }}>
                    <p className="marketing-dashboard-ui-budget-title">Recent Campaigns</p>
                    <table className="marketing-dashboard-ui-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Conversions</th>
                                <th>Created Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentCampaigns.map((c, idx) => (
                                <tr key={idx}>
                                    <td>{c.name}</td>
                                    <td>
                                        <span className={`marketing-dashboard-ui-status ${c.status.toLowerCase()}`}>
                                            {c.status}
                                        </span>
                                    </td>
                                    <td>{c.conversions}</td>
                                    <td>{new Date(c.created_time).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* === NEW LAST SECTION === */}
 <div className="marketing-dashboard-ui-trends-header">
                    <div className="marketing-dashboard-ui-trends-title">Performance Trends</div>
                    <div className="marketing-dashboard-ui-controls">


                    


                        
                    </div>
                </div>

                <div className="marketing-dashboard-ui-charts-row">
                    <div className="marketing-dashboard-ui-chart-container" >
                        <div className="marketing-dashboard-ui-chart-header">
                            <p className="marketing-dashboard-ui-chart-title">Clicks & Impressions Over Time</p>
                            {/* <div className="marketing-dashboard-ui-legend">
                                <span className="marketing-dashboard-ui-legend-item" style={{ '--dot-color': '#3b82f6' }}>Clicks</span>
                                <span className="marketing-dashboard-ui-legend-item" style={{ '--dot-color': '#10b981' }}>Impressions</span>
                            </div> */}
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <PerformanceTrendsChart selectedaccount={selectedAccount} />
                      

                        </ResponsiveContainer>
                    </div>
                   <BudgetAllocationChart 
    selectedaccount={selectedAccount}
    range_type={"last_30_days"} 
/>
                </div>
        </div>
    );
};

// --- Main Dashboard ---
const Dashboardtest = () => {
    const userdata = useSelector((store) => store.userReducer.userdata);

    const [activeDashboardType, setActiveDashboardType] = useState('post');
    const [facebookAccounts, setFacebookAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [isPlatformDropdownOpen, setIsPlatformDropdownOpen] = useState(false);
    const platformRef = useRef(null);

    const [dashboardCounts, setDashboardCounts] = useState({
        active_campaigns: 0,
        completed_campaigns: 0,
        avg_conversion_rate: 0,
        total_budget_used: 0
    });
    const [recentCampaigns, setRecentCampaigns] = useState([]);
    const [weeklyData, setWeeklyData] = useState({ clicks: [], conversions: [], weeks: [] });

    // Fetch Ad Accounts
    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const res = await axios.get(apiUrl + 'facebook_ad_accounts', {
                    headers: { Authorization: 'Bearer ' + userdata?.token },
                });
                const accounts = res.data?.data?.data || [];
                setFacebookAccounts(accounts);
                if (accounts.length > 0) {
                    setSelectedAccount(accounts[0].id);
                    fetchAllData(accounts[0].id);
                }
            } catch (err) {
                console.error('❌ Fetch Accounts Error', err);
            }
        };
        fetchAccounts();
    }, [userdata]);

    const fetchAllData = async (adAccountId) => {
        try {
            const countsRes = await axios.get(apiUrl + 'facebook_dashboard_campaigns_counts', {
                params: { ad_account_id: adAccountId },
                headers: { Authorization: 'Bearer ' + userdata?.token }
            });
            setDashboardCounts(countsRes.data?.data?.data || {});

            const recentRes = await axios.get(apiUrl + 'facebook_dashboard_recent_campaigns', {
                params: { ad_account_id: adAccountId },
                headers: { Authorization: 'Bearer ' + userdata?.token }
            });
            setRecentCampaigns(recentRes.data?.data?.data || []);

            const weeklyRes = await axios.get(apiUrl + 'facebook_weekly_combined_data', {
                params: { ad_account_id: adAccountId },
                headers: { Authorization: 'Bearer ' + userdata?.token }
            });
            setWeeklyData({ ...weeklyRes.data?.data?.data });
        } catch (err) {
            console.error('❌ Fetch Dashboard Data Error', err);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (platformRef.current && !platformRef.current.contains(event.target)) {
                setIsPlatformDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="marketing-dashboard-ui-main-content">
            {/* Section Header with Toggle & Dropdown */}
            <div className="marketing-dashboard-ui-section-header">
                <div className="marketing-dashboard-ui-toggle-group">
                    <button
                        className={`marketing-dashboard-ui-toggle-btn ${activeDashboardType === 'post' ? 'active' : ''}`}
                        onClick={() => setActiveDashboardType('post')}
                    >
                        Post
                    </button>
                    <button
                        className={`marketing-dashboard-ui-toggle-btn ${activeDashboardType === 'campaign' ? 'active' : ''}`}
                        onClick={() => setActiveDashboardType('campaign')}
                    >
                        Campaign
                    </button>

                    <div className="dashboard-dropdown" style={{ marginLeft: '20px', display: 'inline-block' }} ref={platformRef}>
                        <button
                            className="marketing-dashboard-ui-platform-btn"
                            onClick={() => setIsPlatformDropdownOpen(!isPlatformDropdownOpen)}
                        >
                            Account: {facebookAccounts.find(acc => acc.id === selectedAccount)?.name || 'Select Account'}
                        </button>
                        {isPlatformDropdownOpen && (
                            <div className="dashboard-dropdown-menu">
                                {facebookAccounts.map(account => (
                                    <div
                                        key={account.id}
                                        className="dashboard-dropdown-item"
                                        onClick={() => {
                                            setSelectedAccount(account.id);
                                            setIsPlatformDropdownOpen(false);
                                            fetchAllData(account.id);
                                        }}
                                    >
                                        {account.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Dashboard Content */}
            <div className="marketing-dashboard-ui-dashboard-content-area">
                {activeDashboardType === 'post' ? (
                    <PostDashboardView />
                ) : (
                    <CampaignDashboardView
                        dashboardCounts={dashboardCounts}
                        recentCampaigns={recentCampaigns}
                        weeklyData={weeklyData}
                        selectedAccount={selectedAccount}
                    />
                )}
            </div>
        </div>
    );
};

export default Dashboardtest;
